import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Clock, Coffee, MessageSquareWarning, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/hooks/useToast';
import * as api from '@/api/supervision.api';
import { useVeRuta } from '@/hooks/useMenu';
import type { Periodo } from '@/api/tienda.api';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { dinero, numero } from '@/lib/formato-tienda';
import { cn } from '@/lib/cn';

/**
 * SUPERVISIÓN — cómo atiende cada asesora.
 *
 * Todo sale de la base: chats, mensajes y ventas. Donde no hay dato sale «—», no un
 * cero inventado: «0 min de respuesta» y «todavía no contestó ninguno» no son lo mismo.
 */

const PERIODOS: Array<{ id: Periodo; etiqueta: string }> = [
  { id: 'hoy', etiqueta: 'Hoy' },
  { id: 'semana', etiqueta: 'Esta semana' },
  { id: 'mes', etiqueta: 'Este mes' },
];

function duracion(min: number | null): string {
  if (min == null) return '—';
  if (min < 1) return 'menos de 1 min';
  if (min < 60) return `${Math.round(min)} min`;
  const h = Math.floor(min / 60);
  return `${h} h ${Math.round(min - h * 60)} min`;
}

function Dato({ titulo, valor, alerta }: { titulo: string; valor: string; alerta?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{titulo}</dt>
      <dd
        className={cn(
          'text-lg font-semibold tabular-nums text-slate-900 dark:text-slate-100',
          alerta && 'text-red-600 dark:text-red-400',
        )}
      >
        {valor}
      </dd>
    </div>
  );
}

/** De dónde salen las ventas, en el orden en que se lee: lo nuevo, lo que volvió, lo que repite. */
const ORIGENES: Array<{ id: api.OrigenDeVenta; etiqueta: string; ayuda: string }> = [
  { id: 'nuevaPorAnuncio', etiqueta: 'Cliente nuevo por anuncio', ayuda: 'Primera compra; llegó por un anuncio' },
  { id: 'carteraPorAnuncio', etiqueta: 'Cartera que volvió por anuncio', ayuda: 'Ya había comprado y un anuncio lo trajo de vuelta' },
  { id: 'nuevaQueRepite', etiqueta: 'Vino por anuncio y repite', ayuda: 'Llegó por un anuncio antes y vuelve a comprar' },
  { id: 'carteraSola', etiqueta: 'Cartera que volvió sola', ayuda: 'Ya era cliente y volvió sin anuncio' },
  { id: 'nuevaSinAnuncio', etiqueta: 'Cliente nuevo sin anuncio', ayuda: 'Primera compra; directo o referido' },
];

export default function Supervision() {
  const [periodo, setPeriodo] = useState<Periodo>('hoy');
  const toast = useToast();
  const qc = useQueryClient();
  const terminar = useMutation({
    mutationFn: (userId: string) => api.terminarPausa(userId),
    onSuccess: () => {
      toast.success('Pausa terminada: vuelve a recibir chats.');
      void qc.invalidateQueries({ queryKey: ['supervision'] });
      void qc.invalidateQueries({ queryKey: ['pausas'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo'),
  });
  const creaCuentas = useVeRuta('/users');
  const { data, isLoading, isError } = useQuery({
    queryKey: ['supervision', periodo],
    queryFn: () => api.supervision(periodo),
    refetchInterval: 60_000,
  });
  const esperando = useQuery({
    queryKey: ['supervision', 'esperando'],
    queryFn: api.esperando,
    refetchInterval: 60_000,
  });

  const filas = data?.filas ?? [];
  const asesoras = filas.filter((f) => f.userId !== null);
  const suma = (k: 'chatsAbiertos' | 'sinContestar' | 'ventasConfirmadas' | 'totalVendido') =>
    filas.reduce((a, f) => a + f[k], 0);
  const plazo = data?.minutosParaContestar ?? esperando.data?.minutosParaContestar ?? 15;

  return (
    <div className="space-y-5">
      <ModuleHeader
        titulo="Supervisión"
        subtitulo="Cómo atiende cada asesora: lo que tiene en sus manos ahora y lo que hizo en el período."
        acciones={
          <div className="flex rounded-full border border-surface-border bg-surface p-0.5">
            {PERIODOS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPeriodo(p.id)}
                aria-pressed={periodo === p.id}
                className={cn(
                  'rounded-full px-3 py-1.5 text-sm transition-colors',
                  periodo === p.id
                    ? 'bg-brand-600 font-medium text-white'
                    : 'text-slate-600 hover:text-slate-900 dark:text-slate-300',
                )}
              >
                {p.etiqueta}
              </button>
            ))}
          </div>
        }
        kpis={
          <>
            <Kpi titulo="Chats abiertos" valor={numero(suma('chatsAbiertos'))} pie="ahora mismo" />
            <Kpi
              titulo="Sin contestar"
              valor={numero(suma('sinContestar'))}
              pie="lo último lo escribió el cliente"
              tono={suma('sinContestar') > 0 ? 'malo' : 'normal'}
            />
            <Kpi
              titulo="Chats nuevos"
              valor={numero(data?.origen.chatsNuevos ?? 0)}
              pie={
                data
                  ? `${numero(data.origen.porAnuncio)} por anuncio · ${numero(data.origen.referidos)} referido${data.origen.referidos === 1 ? '' : 's'} · ${numero(data.origen.directos)} directos`
                  : undefined
              }
            />
            <Kpi
              titulo="Vendido"
              valor={dinero(suma('totalVendido'))}
              pie={`${numero(suma('ventasConfirmadas'))} venta${suma('ventasConfirmadas') === 1 ? '' : 's'} confirmada${suma('ventasConfirmadas') === 1 ? '' : 's'}`}
            />
          </>
        }
      />

      {isLoading && (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      )}
      {isError && (
        <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
          No se pudo cargar la supervisión. Recarga la página.
        </p>
      )}

      {data && asesoras.length === 0 && (
        <p className="rounded-lg border border-dashed border-surface-border px-4 py-8 text-center text-sm text-slate-500">
          {/* Las cuentas las crea quien administra el sistema: al encargado no se le manda a
              una pantalla que no tiene. */}
          {creaCuentas ? (
            <>
              Todavía no hay asesoras. Invítalas desde <Link className="underline" to="/users">Usuarios</Link> con
              el rol «Agente»: desde ese momento los chats nuevos se reparten entre ellas y salen aquí.
            </>
          ) : (
            'Todavía no hay asesoras. Sus cuentas las crea la administración del sistema: en cuanto existan, los chats nuevos se reparten entre ellas y salen aquí.'
          )}
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {filas.map((f) => (
          <section
            key={f.userId ?? 'sin-asignar'}
            className="rounded-2xl border border-surface-border bg-surface p-5"
          >
            <div className="flex items-start justify-between gap-2">
              <h2 className="min-w-0 truncate text-lg font-semibold text-slate-900 dark:text-slate-100">
                {f.nombre}
              </h2>
              {f.userId === null ? (
                <Badge variant="warning">Nadie los atiende</Badge>
              ) : f.pausa ? (
                <Badge variant="info">
                  <Coffee className="mr-1 inline h-3 w-3" />
                  {f.pausa.etiqueta} · {duracion((Date.now() - new Date(f.pausa.desde).getTime()) / 60_000)}
                </Badge>
              ) : f.recibeChats ? (
                <Badge variant="success">Recibe chats</Badge>
              ) : (
                <Badge variant="default">No recibe chats</Badge>
              )}
            </div>

            <p className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Ahora</p>
            <dl className="mt-1 grid grid-cols-3 gap-3">
              <Dato titulo="Chats abiertos" valor={numero(f.chatsAbiertos)} />
              <Dato titulo="Sin contestar" valor={numero(f.sinContestar)} alerta={f.sinContestar > 0} />
              <Dato
                titulo="Espera más larga"
                valor={duracion(f.esperaMaximaMin)}
                alerta={(f.esperaMaximaMin ?? 0) >= plazo}
              />
            </dl>

            {f.userId !== null && (
              <>
                <p className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {PERIODOS.find((p) => p.id === periodo)?.etiqueta}
                </p>
                <dl className="mt-1 grid grid-cols-3 gap-3">
                  <Dato titulo="Le tocaron" valor={numero(f.recibidos)} />
                  <Dato titulo="Contestó" valor={`${numero(f.atendidos)} de ${numero(f.recibidos)}`} />
                  <Dato titulo="1.ª respuesta" valor={duracion(f.primeraRespuestaMin)} />
                  <Dato titulo="Ventas pasadas" valor={numero(f.ventasConfirmadas)} />
                  <Dato titulo="Vendido" valor={dinero(f.totalVendido)} />
                  <Dato titulo="En borrador" valor={numero(f.ventasEnBorrador)} />
                  <Dato titulo="En pausa" valor={f.minutosEnPausa > 0 ? duracion(f.minutosEnPausa) : '—'} />
                </dl>
                {f.pausa && (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-800 dark:bg-sky-900/20 dark:text-sky-200">
                    <span>
                      No recibe chats nuevos.{' '}
                      {f.pausa.apoyo ? 'Sus compañeras ven y contestan los suyos.' : 'Sus chats esperan por ella.'}
                    </span>
                    <Button size="sm" variant="outline" onClick={() => terminar.mutate(f.userId!)} disabled={terminar.isPending}>
                      Terminar pausa
                    </Button>
                  </div>
                )}
              </>
            )}
          </section>
        ))}
      </div>

      {data && (
        <section className="rounded-2xl border border-surface-border bg-surface p-5">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
            <Megaphone className="h-5 w-5 text-brand-600" />
            De dónde salen las ventas
          </h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Solo ventas confirmadas: lo que se vendió de verdad, no lo que se habló en el chat. Un cliente de antes no
            se le cuenta entero a la publicidad.
          </p>
          <ul className="mt-3 divide-y divide-surface-border">
            {ORIGENES.map((o) => {
              const d = data.atribucion?.[o.id] ?? { ventas: 0, total: 0 };
              return (
                <li key={o.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                  <span className="min-w-0">
                    <span className="block font-medium text-slate-900 dark:text-slate-100">{o.etiqueta}</span>
                    <span className="block text-xs text-slate-500">{o.ayuda}</span>
                  </span>
                  <span className="text-sm tabular-nums text-slate-600 dark:text-slate-300">
                    {numero(d.ventas)} venta{d.ventas === 1 ? '' : 's'} · <strong>{dinero(d.total)}</strong>
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="rounded-2xl border border-surface-border bg-surface p-5">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
          <MessageSquareWarning className="h-5 w-5 text-amber-600" />
          Clientes esperando más de {plazo} min
        </h2>
        <p className="mt-0.5 text-sm text-slate-500">
          A la asesora le sale el mismo aviso arriba de su pantalla. El sistema no mueve el chat solo: eso lo
          decides tú, abriéndolo.
        </p>
        {(esperando.data?.chats ?? []).length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">Nadie está esperando.</p>
        ) : (
          <ul className="mt-3 divide-y divide-surface-border">
            {esperando.data!.chats.map((c) => (
              <li key={c.conversationId} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                <Link
                  to={`/conversations/${c.conversationId}`}
                  className="min-w-0 truncate font-medium text-slate-900 underline-offset-2 hover:underline dark:text-slate-100"
                >
                  {c.cliente}
                </Link>
                <span className="flex items-center gap-3 text-sm text-slate-500">
                  <span>{c.asesora ?? 'Sin asignar'}</span>
                  <span className="inline-flex items-center gap-1 font-medium text-red-600 dark:text-red-400">
                    <Clock className="h-3.5 w-3.5" />
                    {duracion(c.minutos)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
